import { useState, useEffect } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Building2, DollarSign, Users, Calendar, TrendingUp, TrendingDown,
  RefreshCw, MapPin, BarChart3, Crown, ArrowUpRight, ArrowDownRight
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const MoMBadge = ({ value }) => {
  if (!value) return null;
  const positive = value >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${positive ? "text-emerald-600" : "text-rose-600"}`}>
      {positive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
      {Math.abs(value)}%
    </span>
  );
};

export default function ExecutiveDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/executive/rollup").then(r => setData(r.data)).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 flex items-center justify-center min-h-[400px]"><RefreshCw className="w-5 h-5 animate-spin text-rose-400" /></div>;

  const s = data?.summary || {};
  const locs = data?.locations || [];

  return (
    <div data-testid="executive-dashboard" className="p-6 lg:p-8 max-w-[1500px] mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-['Outfit'] text-2xl font-semibold text-slate-900 tracking-tight">Executive Dashboard</h1>
          <p className="text-sm text-slate-400 mt-1">Multi-location roll-up &middot; {s.location_count || 0} locations</p>
        </div>
        <Button size="sm" onClick={() => { setLoading(true); api.get("/executive/rollup").then(r => setData(r.data)).finally(() => setLoading(false)); }} className="bg-slate-900 text-white" data-testid="refresh-exec-btn">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Global Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div data-testid="stat-total-rev" className="bg-gradient-to-br from-[#1A1A1A] to-[#333] rounded-2xl p-5 text-white">
          <DollarSign className="w-5 h-5 text-white/60 mb-2" />
          <p className="font-['Outfit'] text-2xl font-semibold">${s.total_month_revenue?.toLocaleString() || 0}</p>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-xs text-white/60">Month Revenue</p>
            <MoMBadge value={s.mom_change} />
          </div>
        </div>
        <div data-testid="stat-today-appts" className="bg-white border border-slate-200 rounded-2xl p-5">
          <Calendar className="w-5 h-5 text-[#8B5CF6] mb-2" />
          <p className="font-['Outfit'] text-2xl font-semibold text-slate-900">{s.total_today_appointments || 0}</p>
          <p className="text-xs text-slate-400 mt-1">Today's Appointments</p>
        </div>
        <div data-testid="stat-clients" className="bg-white border border-slate-200 rounded-2xl p-5">
          <Users className="w-5 h-5 text-emerald-600 mb-2" />
          <p className="font-['Outfit'] text-2xl font-semibold text-slate-900">{s.total_clients || 0}</p>
          <p className="text-xs text-slate-400 mt-1">Total Clients</p>
        </div>
        <div data-testid="stat-memberships" className="bg-white border border-slate-200 rounded-2xl p-5">
          <Crown className="w-5 h-5 text-[#F59E0B] mb-2" />
          <p className="font-['Outfit'] text-2xl font-semibold text-slate-900">{s.active_memberships || 0}</p>
          <p className="text-xs text-slate-400 mt-1">Active Memberships</p>
        </div>
      </div>

      {/* Per-Location Breakdown */}
      <div className="space-y-6">
        {locs.map(loc => (
          <div key={loc.location_id} data-testid={`loc-card-${loc.location_id}`} className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            {/* Location Header */}
            <div className="px-6 py-4 border-b border-slate-200 bg-[#FAFAF9] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="font-['Outfit'] text-lg font-semibold text-slate-900">{loc.name}</h2>
                  <p className="text-xs text-slate-400">{loc.address}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="font-['Outfit'] text-xl font-semibold text-slate-900">${loc.month_revenue?.toLocaleString()}</p>
                  <div className="flex items-center gap-1 justify-end">
                    <p className="text-xs text-slate-400">This month</p>
                    <MoMBadge value={loc.mom_change} />
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6">
              {/* KPIs */}
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-5">
                <div className="text-center p-2">
                  <p className="text-xs text-slate-400">Today Appts</p>
                  <p className="font-['Outfit'] text-lg font-semibold text-slate-900">{loc.today_appointments}</p>
                </div>
                <div className="text-center p-2">
                  <p className="text-xs text-slate-400">Week Rev</p>
                  <p className="font-['Outfit'] text-lg font-semibold text-emerald-600">${loc.week_revenue?.toLocaleString()}</p>
                </div>
                <div className="text-center p-2">
                  <p className="text-xs text-slate-400">Avg Ticket</p>
                  <p className="font-['Outfit'] text-lg font-semibold text-slate-900">${loc.avg_ticket}</p>
                </div>
                <div className="text-center p-2">
                  <p className="text-xs text-slate-400">Completed</p>
                  <p className="font-['Outfit'] text-lg font-semibold text-emerald-600">{loc.completed}</p>
                </div>
                <div className="text-center p-2">
                  <p className="text-xs text-slate-400">No-Shows</p>
                  <p className="font-['Outfit'] text-lg font-semibold text-rose-600">{loc.noshows}</p>
                </div>
                <div className="text-center p-2">
                  <p className="text-xs text-slate-400">Clients</p>
                  <p className="font-['Outfit'] text-lg font-semibold text-slate-900">{loc.total_clients}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Revenue Chart */}
                <div>
                  <p className="text-xs font-medium text-slate-400 mb-2 uppercase tracking-wider">7-Day Revenue</p>
                  <div className="h-[150px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={loc.daily_revenue || []} barCategoryGap="15%">
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E6E4E0" />
                        <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#8A8A8A" }} axisLine={false} tickLine={false} tickFormatter={d => d.slice(5)} />
                        <YAxis tick={{ fontSize: 10, fill: "#8A8A8A" }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} />
                        <Tooltip formatter={v => [`$${v}`, "Revenue"]} contentStyle={{ borderRadius: 8, fontSize: 11, border: "1px solid #E6E4E0" }} />
                        <Bar dataKey="revenue" fill="#1A1A1A" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Provider Leaderboard */}
                <div>
                  <p className="text-xs font-medium text-slate-400 mb-2 uppercase tracking-wider">Provider Performance</p>
                  <div className="space-y-2">
                    {(loc.providers || []).sort((a, b) => b.revenue - a.revenue).map((p, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold" style={{ backgroundColor: p.color }}>
                          {p.name?.charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-medium text-slate-900 truncate">{p.name}</p>
                            <p className="text-xs font-semibold text-emerald-600">${p.revenue?.toLocaleString()}</p>
                          </div>
                          <div className="h-1.5 bg-slate-50 rounded-full mt-1 overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${Math.min(p.revenue / Math.max(...(loc.providers || []).map(pr => pr.revenue), 1) * 100, 100)}%`, backgroundColor: p.color }} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
